import { ADDON_PRODUCTS, DEDICATED_KINDS, dedicatedProductForKind, normalizeDedicatedKind } from './config.js';
import { buildCheckoutItems, priceMeta, resolveCatalogPrice } from './priceCatalog.js';
import { resolveCheckoutUpdates } from './subscriptionPlan.js';
import { addonEntitlementsFromAccount } from './addonEntitlements.js';

export { addonEntitlementsFromAccount };

/** @typedef {{ analyzer: boolean, dedicated: boolean, mcp?: boolean }} AddonEntitlements */

// Canonical checkout-selection for resolveFullCheckoutUpdates. Accepts both the
// raw store selection (`dedicated*`) and compat state (`dedicatedEnv*`) so
// callers can't drop a DE field (e.g. dedicatedKinds).
export function toCheckoutSelection(src) {
  if (!src) return {};
  return {
    billingCycle: src.billingCycle,
    standardCount: src.standardCount,
    premiumCount: src.premiumCount,
    futuresCount: src.futuresCount,
    futuresUnlimited: src.futuresUnlimited,
    analyzerEnabled: src.analyzerEnabled,
    dedicated: src.dedicated ?? src.dedicatedEnvs,
    dedicatedSubIds: src.dedicatedSubIds ?? src.dedicatedEnvSubIds,
    dedicatedKinds: src.dedicatedKinds ?? src.dedicatedEnvKinds,
    mcpEnabled: src.mcpEnabled
  };
}

/** Copier accounts in the cart: Standard, Premium or Futures. Analyzer is sold only alongside them. */
export function hasCopierSeats(selection) {
  return (
    (selection.standardCount || 0) + (selection.premiumCount || 0) + (selection.futuresCount || 0) > 0 ||
    Boolean(selection.futuresUnlimited)
  );
}

/**
 * @param {{ analyzerEnabled?: boolean, dedicated?: number[], billingCycle: string }} selection
 * @param {object} catalog
 * @param {{ subscriptions?: object[] }} [options]
 */
export function buildAddonCheckoutItems(selection, catalog, options = {}) {
  if (!catalog) return [];

  const items = [];
  const cycle = selection.billingCycle;

  // Without copier accounts the drawer shows Analyzer off, so a choice left staged never reaches Stripe.
  if (selection.analyzerEnabled && hasCopierSeats(selection)) {
    const price = resolveCatalogPrice(catalog, ADDON_PRODUCTS.analyzer, cycle, options);
    if (price?.id) {
      items.push({ price_id: price.id, quantity: 1 });
    }
  }

  // AI Trading (MCP) — flat standalone add-on (like Dedicated Env, not
  // copier-gated). One line for the selected cadence's mcp_<cycle> price.
  if (selection.mcpEnabled) {
    const price = resolveCatalogPrice(catalog, ADDON_PRODUCTS.mcp, cycle, options);
    if (price?.id) {
      items.push({ price_id: price.id, quantity: 1 });
    }
  }

  // One line item per env, priced by its kind (CFD vs Futures carry different
  // price_ids). CFD sends the account count; Futures is flat/unlimited → qty 1.
  const envs = Array.isArray(selection.dedicated) ? selection.dedicated : [];
  const kinds = Array.isArray(selection.dedicatedKinds) ? selection.dedicatedKinds : [];
  envs.forEach((accts, i) => {
    const isFutures = normalizeDedicatedKind(kinds[i]) === DEDICATED_KINDS.futures;
    const price = resolveCatalogPrice(catalog, dedicatedProductForKind(kinds[i]), cycle, options);
    if (price?.id) {
      items.push({
        price_id: price.id,
        quantity: isFutures ? 1 : Math.max(1, Number(accts) || 1)
      });
    }
  });

  return items;
}

/**
 * Single source of truth for the items[] payload sent to checkout — combines
 * copier seat lines + addon lines (analyzer + per-env DE). Used by both the
 * router-driven `/apply` flow and the public guest checkout.
 *
 * @param {object} selection
 * @param {object} catalog
 * @param {{ subscriptions?: object[] }} [options]
 * @returns {{ price_id: string, quantity: number }[]}
 */
export function buildFullCheckoutItems(selection, catalog, options = {}) {
  if (!catalog) return [];
  return [...buildCheckoutItems(selection, catalog, options), ...buildAddonCheckoutItems(selection, catalog, options)];
}

/**
 * Full checkout plan = copier items + addon items routed through the
 * cycle-keyed router. Returns the router's multi-cycle / noop shape.
 *
 * @param {object} selection
 * @param {object} catalog
 * @param {object[]} subscriptions
 * @param {AddonEntitlements} currentAddons
 * @param {object} [trialContext]
 */
export function resolveFullCheckoutUpdates(selection, catalog, subscriptions, currentAddons, trialContext = null) {
  const items = buildFullCheckoutItems(selection, catalog, { subscriptions });

  // Drop analyzer/MCP if already entitled — both are single-instance per user,
  // so the router would otherwise emit a duplicate AddItem/CreateSubscription.
  const filtered = items.filter((item) => {
    const meta = priceMeta(catalog?.getById?.(item.price_id));
    if (meta?.product === ADDON_PRODUCTS.analyzer && currentAddons?.analyzer) {
      return false;
    }
    if (meta?.product === ADDON_PRODUCTS.mcp && currentAddons?.mcp) {
      return false;
    }
    return true;
  });

  return resolveCheckoutUpdates(filtered, subscriptions, catalog, trialContext);
}
